import { combineLatest, Observable, of, tap } from 'rxjs'
import { switchMap, takeUntil } from 'rxjs/operators'

import { ConfigurableProductDetailsFragment } from '../../../graphql/magento'
import {
  AttributeSkuMapping,
  FormattedVariantData,
  formattedVariantData,
} from '../../../utils/itemTools'
import { left, Result, right } from '../../../utils/result'
import { tapLeft, tapRight } from '../../../utils/rx/operators'
import { closeQuickViewProductModal } from '../quick-view-modal'
import { configurableProductByUrlKey, sanityParentProductQuery } from './data-fetching'
import {
  OpenProductModalInput,
  ProductModalData,
  ProductModalState,
  ProductModalStateMethods,
} from './types'

export const productModalStateMethods = <
  TData extends ProductModalData,
>(): ProductModalStateMethods<TData> => ({
  closed: () => ({ type: 'CLOSED' }),
  loading: () => ({ type: 'LOADING' }),
  error: (error) => ({ type: 'ERROR', error }),
  open: (data) => ({ type: 'OPEN', data }),
})

export const variantDataFromItem = (
  item: ConfigurableProductDetailsFragment,
): { defaultChildSku: string; variantData: FormattedVariantData } => {
  const variantData = formattedVariantData({ variant_index: item.variant_index || {} })
  const { usedAttributes } = variantData

  const defaultChildSku = usedAttributes.reduce(
    (check, att) => check[variantData.attributeValues[att][0]] as AttributeSkuMapping,
    variantData.mapping,
  ) as unknown as string

  return { defaultChildSku, variantData }
}

const handlePopStateEvent = (_event: PopStateEvent) => {
  if (document.location.hash !== '#quick-view') {
    closeQuickViewProductModal()
  }
}

export const processProductModalSignals = <
  TInput extends OpenProductModalInput,
  TData extends ProductModalData,
>({
  productModalState,
  openProductModal$,
  closeProductModal$,
  setProductModalState,
}: {
  productModalState: ProductModalStateMethods<TData>
  openProductModal$: Observable<TInput>
  closeProductModal$: Observable<void>
  setProductModalState: (state: ProductModalState<TData>) => void
}): { open$: Observable<Result<ProductModalState<TData>, Error>>; close$: Observable<void> } => {
  // when an item finishes loading set the modal state to
  // open with the item data if it is a configurable product
  // or error if it is not
  const open$ = openProductModal$.pipe(
    tap(() => {
      if (typeof window === 'undefined') {
        return
      }
      const pathname = window.location.pathname
      window.history.pushState({ quickView: 'quick-view' }, 'Quick view', `${pathname}#quick-view`)
      window.addEventListener('popstate', handlePopStateEvent)
    }),
    tap(() => setProductModalState(productModalState.loading())),
    switchMap(({ productDetails, urlKey, sku, ...rest }) =>
      combineLatest([
        configurableProductByUrlKey(urlKey),
        productDetails ? of(right(productDetails)) : sanityParentProductQuery(sku),
      ]).pipe(
        takeUntil(closeProductModal$),
        switchMap(([configurableProductResult, productDetailsResult]) =>
          configurableProductResult._tag === 'Left'
            ? of(left(configurableProductResult.error))
            : of(
                right(
                  productDetailsResult._tag === 'Left'
                    ? productModalState.closed()
                    : productModalState.open({
                        item: configurableProductResult.data,
                        ...variantDataFromItem(configurableProductResult.data),
                        openingSku: sku,
                        openingUrlKey: urlKey,
                        productDetails: productDetailsResult.data,
                        ...rest,
                      } as unknown as TData),
                ),
              ),
        ),
      ),
    ),
    tapRight(({ data: state }) => setProductModalState(state)),
    tapLeft(({ error }) => setProductModalState(productModalState.error(error))),
  )

  const close$ = closeProductModal$.pipe(
    tap(() => {
      if (typeof window === 'undefined') {
        return
      }
      window.history.replaceState({}, '', window.location.pathname)
      window.removeEventListener('popstate', handlePopStateEvent)
    }),
    tap(() => setProductModalState(productModalState.closed())),
  )

  return { open$, close$ }
}
